import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Music4, PenLine, Mic2, Download, Zap, Layers, SlidersHorizontal,
  Voicemail, Youtube, Gift, Image as ImageIcon, Repeat, Sparkles, Check, ArrowRight,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO, { SITE_URL } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";

const SERVICES = [
  {
    id: "chanson-personnalisee-ia", icon: Music4,
    fr: {
      title: "Chanson personnalisée par IA : votre musique sur mesure",
      intro: "Kalypso Music est un générateur de musique par intelligence artificielle qui crée une chanson personnalisée unique à partir de votre histoire. Que ce soit pour un mariage, un anniversaire, une naissance, un départ à la retraite ou simplement pour offrir un cadeau musical original, notre créateur de chanson en ligne transforme vos souvenirs en une véritable musique professionnelle.",
      details: [
        "Chanson 100% originale et unique, créée spécialement pour vous ou la personne de votre choix",
        "Personnalisation complète : prénom, histoire, anecdotes, dates et messages intégrés dans les paroles",
        "Idéale comme cadeau musical original pour toutes les occasions : mariage, anniversaire, naissance, Noël, Saint-Valentin, retraite",
        "Création simple en quelques clics grâce à un assistant guidé pas à pas, sans aucune connaissance musicale",
        "Durée au choix : 2, 3 ou 4 minutes de musique complète avec couplets et refrains",
        "Un prix unique, transparent et accessible, sans frais cachés ni engagement",
        "Résultat professionnel comparable à un studio d'enregistrement, livré en ligne",
        "Compatible mobile, tablette et ordinateur : créez votre chanson personnalisée où que vous soyez",
        "Essai gratuit disponible pour découvrir la qualité avant de commander",
        "Des centaines de clients satisfaits ont déjà offert leur chanson sur mesure",
      ],
    },
    en: {
      title: "AI personalized song: your custom-made music",
      intro: "Kalypso Music is an AI music generator that creates a unique personalized song from your story. For a wedding, a birthday, a birth, a retirement or simply an original musical gift, our online song maker turns your memories into professional-quality music.",
      details: [
        "100% original and unique song, created especially for you or your loved one",
        "Full personalization: names, story, anecdotes, dates and messages woven into the lyrics",
        "Perfect as an original musical gift for every occasion: wedding, birthday, birth, Christmas, Valentine's Day, retirement",
        "Simple creation in a few clicks with a guided step-by-step wizard, no musical knowledge required",
        "Choose your duration: 2, 3 or 4 minutes of complete music with verses and choruses",
        "One flat, transparent and affordable price, with no hidden fees and no commitment",
        "Professional result comparable to a recording studio, delivered online",
        "Works on mobile, tablet and desktop: create your personalized song anywhere",
        "Free trial available to hear the quality before ordering",
        "Hundreds of happy customers have already gifted their custom song",
      ],
    },
  },
  {
    id: "paroles-sur-mesure", icon: PenLine,
    fr: {
      title: "Paroles de chanson sur mesure écrites par IA",
      intro: "Nos paroles de chanson personnalisées sont écrites par une intelligence artificielle de dernière génération, entraînée à raconter des histoires touchantes, drôles ou émouvantes. Chaque texte est unique et construit autour de vos informations.",
      details: [
        "Paroles 100% personnalisées à partir de votre histoire, vos souvenirs et vos anecdotes",
        "Intégration naturelle des prénoms, lieux, dates et private jokes dans le texte",
        "Ton au choix : émouvant, drôle, romantique, motivant, festif ou solennel",
        "Structure professionnelle : couplets, refrains mémorables et pont musical",
        "Possibilité de fournir vos propres paroles si vous préférez écrire vous-même",
        "Adaptation automatique des rimes et du rythme au style musical choisi",
        "Paroles disponibles en plusieurs langues pour toucher tous vos proches",
        "Relecture et aperçu des paroles avant la génération finale de la musique",
        "Aucune faute de goût : un texte respectueux, adapté à l'occasion et au destinataire",
        "Les paroles complètes vous sont livrées avec la chanson pour les imprimer ou les encadrer",
      ],
    },
    en: {
      title: "Custom song lyrics written by AI",
      intro: "Our personalized song lyrics are written by state-of-the-art AI, trained to tell touching, funny or moving stories. Every text is unique and built around your details.",
      details: [
        "100% personalized lyrics from your story, memories and anecdotes",
        "Names, places, dates and inside jokes woven naturally into the text",
        "Choose the tone: emotional, funny, romantic, motivational, festive or solemn",
        "Professional structure: verses, memorable choruses and a musical bridge",
        "Option to provide your own lyrics if you prefer to write them yourself",
        "Rhymes and rhythm automatically adapted to the chosen musical style",
        "Lyrics available in several languages to reach all your loved ones",
        "Preview the lyrics before the final music generation",
        "Always tasteful: a respectful text suited to the occasion and the recipient",
        "Full lyrics delivered with the song, ready to print or frame",
      ],
    },
  },
  {
    id: "voix-chantee-realiste", icon: Mic2,
    fr: {
      title: "Voix chantée réaliste, mixée et masterisée en qualité studio",
      intro: "La voix chantée de votre musique personnalisée est générée par IA avec un réalisme bluffant : vibrato, émotion, articulation naturelle. Chaque chanson est ensuite mixée et masterisée pour un rendu professionnel digne des plateformes de streaming.",
      details: [
        "Voix chantée ultra-réaliste, impossible à distinguer d'un vrai chanteur pour la plupart des auditeurs",
        "Voix masculine ou féminine au choix selon l'ambiance souhaitée",
        "Plusieurs langues chantées disponibles : français, anglais, espagnol, allemand et plus",
        "Mixage professionnel automatique : équilibre parfait entre voix et instruments",
        "Mastering final pour un volume et une clarté dignes des standards de l'industrie musicale",
        "Interprétation expressive adaptée à l'émotion de vos paroles",
        "Aucun accent robotique : des intonations naturelles et fluides",
        "Qualité audio haute fidélité adaptée aux enceintes, casques et sonorisations d'événements",
        "Parfaite pour être diffusée lors d'un mariage, d'une fête ou d'une cérémonie",
        "Technologie IA de pointe mise à jour en continu pour un réalisme toujours meilleur",
      ],
    },
    en: {
      title: "Realistic sung vocals, mixed and mastered in studio quality",
      intro: "The vocals of your personalized song are AI-generated with stunning realism: vibrato, emotion, natural articulation. Every song is then mixed and mastered for a professional, streaming-ready sound.",
      details: [
        "Ultra-realistic sung vocals, indistinguishable from a real singer for most listeners",
        "Male or female voice depending on the desired vibe",
        "Multiple sung languages available: English, French, Spanish, German and more",
        "Automatic professional mixing: perfect balance between vocals and instruments",
        "Final mastering for loudness and clarity meeting music-industry standards",
        "Expressive interpretation matched to the emotion of your lyrics",
        "No robotic accent: natural, fluid intonation",
        "High-fidelity audio quality for speakers, headphones and event sound systems",
        "Perfect for playing at a wedding, a party or a ceremony",
        "Cutting-edge AI technology continuously updated for ever-better realism",
      ],
    },
  },
  {
    id: "mp3-telechargeable-a-vie", icon: Download,
    fr: {
      title: "MP3 téléchargeable qui vous appartient à vie",
      intro: "Votre musique personnalisée vous est livrée en fichier MP3 haute qualité, téléchargeable immédiatement. Contrairement aux plateformes par abonnement, votre chanson vous appartient pour toute la vie.",
      details: [
        "Téléchargement MP3 haute qualité immédiatement après la génération",
        "Propriété à vie : votre chanson vous appartient, sans abonnement pour continuer à l'écouter",
        "Écoute illimitée sur tous vos appareils : téléphone, ordinateur, voiture, enceinte",
        "Partage libre avec votre famille et vos amis par messagerie ou réseaux sociaux",
        "Fichier compatible avec tous les lecteurs audio et toutes les plateformes",
        "Idéal pour graver un CD souvenir ou créer une clé USB cadeau",
        "Re-téléchargement possible à tout moment depuis votre espace personnel",
        "Aucun filigrane audio ni limitation de durée d'écoute",
        "Usage personnel illimité pour vos événements privés",
        "Conservation sécurisée de vos créations dans votre compte Kalypso Music",
      ],
    },
    en: {
      title: "Downloadable MP3 that's yours for life",
      intro: "Your personalized song is delivered as a high-quality MP3, downloadable instantly. Unlike subscription platforms, your song belongs to you for life.",
      details: [
        "High-quality MP3 download immediately after generation",
        "Lifetime ownership: your song is yours, no subscription needed to keep listening",
        "Unlimited listening on all your devices: phone, computer, car, speakers",
        "Free sharing with family and friends via messaging or social media",
        "File compatible with every audio player and platform",
        "Perfect for burning a keepsake CD or creating a gift USB drive",
        "Re-download anytime from your personal account",
        "No audio watermark and no listening time limits",
        "Unlimited personal use for your private events",
        "Your creations safely stored in your Kalypso Music account",
      ],
    },
  },
  {
    id: "livraison-quelques-minutes", icon: Zap,
    fr: {
      title: "Livraison de votre chanson en quelques minutes",
      intro: "Pas besoin d'attendre des jours ou des semaines comme avec un compositeur traditionnel : votre chanson personnalisée est générée et livrée en quelques minutes seulement, prête à être écoutée et téléchargée.",
      details: [
        "Génération complète de la musique en quelques minutes après validation",
        "Parfait pour les cadeaux de dernière minute : anniversaire ce soir ? C'est possible !",
        "Notification par email dès que votre chanson est prête",
        "Suivi en temps réel de l'avancement de la création dans votre espace",
        "Aucun rendez-vous, aucun délai de production, aucun échange interminable",
        "Disponible 24h/24 et 7j/7, week-ends et jours fériés compris",
        "Régénération rapide en cas de besoin d'ajustement",
        "Beaucoup plus rapide et abordable qu'un studio d'enregistrement traditionnel",
        "Créez plusieurs chansons dans la même journée pour plusieurs occasions",
        "Service fiable avec régénération gratuite en cas d'échec technique",
      ],
    },
    en: {
      title: "Your song delivered in minutes",
      intro: "No need to wait days or weeks like with a traditional composer: your personalized song is generated and delivered in just a few minutes, ready to listen and download.",
      details: [
        "Complete music generation within minutes of confirming",
        "Perfect for last-minute gifts: birthday tonight? It's possible!",
        "Email notification as soon as your song is ready",
        "Real-time progress tracking in your account",
        "No appointments, no production delays, no endless back-and-forth",
        "Available 24/7, including weekends and holidays",
        "Fast regeneration if an adjustment is ever needed",
        "Far faster and more affordable than a traditional recording studio",
        "Create several songs in a single day for multiple occasions",
        "Reliable service with free regeneration in case of technical failure",
      ],
    },
  },
  {
    id: "generations-multiples", icon: Layers,
    fr: {
      title: "Générations multiples : jusqu'à 3 versions de votre chanson",
      intro: "Chaque histoire peut être racontée de plusieurs façons. Avec les générations multiples, recevez jusqu'à 3 versions différentes de votre chanson personnalisée et choisissez celle qui vous touche le plus.",
      details: [
        "Jusqu'à 3 versions musicales différentes générées pour la même histoire",
        "Comparez les interprétations et gardez votre préférée",
        "Variations de mélodie, d'arrangement et d'interprétation vocale",
        "Idéal pour les perfectionnistes qui veulent LA version parfaite",
        "Écoute et comparaison faciles depuis votre espace personnel",
        "Toutes les versions restent accessibles dans votre compte",
        "Augmente vos chances d'un coup de cœur immédiat",
        "Un rapport qualité-prix imbattable par rapport à un compositeur",
        "Sélection de la version finale en un clic",
        "Chaque version bénéficie du même mixage et mastering professionnel",
      ],
    },
    en: {
      title: "Multiple generations: up to 3 versions of your song",
      intro: "Every story can be told in several ways. With multiple generations, receive up to 3 different versions of your personalized song and pick the one that moves you most.",
      details: [
        "Up to 3 different musical versions generated for the same story",
        "Compare interpretations and keep your favorite",
        "Variations in melody, arrangement and vocal interpretation",
        "Perfect for perfectionists who want THE perfect version",
        "Easy listening and comparison from your personal account",
        "All versions remain accessible in your account",
        "Boosts your chances of an instant favorite",
        "Unbeatable value compared to hiring a composer",
        "Pick the final version in one click",
        "Every version gets the same professional mixing and mastering",
      ],
    },
  },
  {
    id: "styles-rythmes-ambiances", icon: SlidersHorizontal,
    fr: {
      title: "De nombreux styles musicaux, rythmes et ambiances",
      intro: "Pop, rock, rap, country, jazz, électro, ballade romantique… Kalypso Music couvre tous les styles musicaux pour créer une musique qui ressemble vraiment à la personne à qui elle est destinée.",
      details: [
        "Large choix de styles : pop, rock, rap, country, jazz, électro, RnB, folk, classique et plus",
        "Ambiances au choix : joyeuse, émouvante, romantique, énergique, motivante, solennelle",
        "Rythmes adaptés : ballade lente, mid-tempo ou hit énergique pour faire danser",
        "Chaque style bénéficie d'instruments et d'arrangements authentiques",
        "Combinez style, ambiance et tempo pour un résultat sur mesure",
        "Suggestions intelligentes selon l'occasion (mariage, anniversaire, hommage…)",
        "Plusieurs langues chantées disponibles quel que soit le style",
        "Écoutez des exemples réels dans notre galerie publique avant de choisir",
        "Des chansons adaptées à tous les âges, des enfants aux grands-parents",
        "Nouveaux styles et ambiances ajoutés régulièrement",
      ],
    },
    en: {
      title: "Many musical styles, rhythms and moods",
      intro: "Pop, rock, rap, country, jazz, electro, romantic ballad… Kalypso Music covers every musical style to craft a song that truly matches its recipient.",
      details: [
        "Wide choice of styles: pop, rock, rap, country, jazz, electro, RnB, folk, classical and more",
        "Moods to choose from: joyful, emotional, romantic, energetic, motivational, solemn",
        "Adapted rhythms: slow ballad, mid-tempo or an energetic dancefloor hit",
        "Every style features authentic instruments and arrangements",
        "Combine style, mood and tempo for a tailor-made result",
        "Smart suggestions based on the occasion (wedding, birthday, tribute…)",
        "Multiple sung languages available in every style",
        "Listen to real examples in our public gallery before choosing",
        "Songs suited to all ages, from kids to grandparents",
        "New styles and moods added regularly",
      ],
    },
  },
  {
    id: "message-vocal-personnalise", icon: Voicemail,
    fr: {
      title: "Votre propre voix dans la musique : le message vocal personnalisé",
      intro: "Un service unique au monde : enregistrez un message avec votre propre voix, et notre technologie l'intègre directement dans la chanson, en intro, en conclusion ou les deux. L'émotion est décuplée quand la personne reconnaît votre voix.",
      details: [
        "Enregistrez votre voix directement depuis votre navigateur, sans matériel spécifique",
        "Votre message vocal est intégré harmonieusement dans la musique",
        "Position au choix : introduction, conclusion, ou les deux",
        "Technologie de clonage vocal de pointe pour un rendu naturel et fluide",
        "Jusqu'à 400 caractères de message parlé avec votre timbre de voix",
        "L'effet émotionnel le plus fort : vos mots, votre voix, dans une vraie chanson",
        "Parfait pour les déclarations d'amour, les vœux de mariage et les hommages",
        "Votre échantillon de voix est conservé en sécurité dans votre profil",
        "Réutilisable pour vos prochaines chansons personnalisées",
        "Une exclusivité Kalypso Music introuvable chez les autres générateurs de musique IA",
      ],
    },
    en: {
      title: "Your own voice inside the music: the personalized voice message",
      intro: "A one-of-a-kind service: record a message with your own voice, and our technology embeds it right into the song, as an intro, an outro or both. The emotion is tenfold when your loved one recognizes your voice.",
      details: [
        "Record your voice straight from your browser, no special equipment needed",
        "Your voice message is blended harmoniously into the music",
        "Choose the position: introduction, conclusion, or both",
        "Cutting-edge voice cloning technology for a natural, fluid result",
        "Up to 400 characters of spoken message with your own voice tone",
        "The strongest emotional effect: your words, your voice, inside a real song",
        "Perfect for love declarations, wedding vows and tributes",
        "Your voice sample is stored securely in your profile",
        "Reusable for your next personalized songs",
        "A Kalypso Music exclusive you won't find in other AI music generators",
      ],
    },
  },
  {
    id: "publication-youtube", icon: Youtube,
    fr: {
      title: "Publication de votre musique sur YouTube",
      intro: "Envie que votre chanson personnalisée vive pour toujours en ligne ? Nous publions votre musique sur YouTube : elle devient accessible à tous, à tout moment, et vous pouvez la partager d'un simple lien.",
      details: [
        "Publication de votre chanson sur YouTube avec sa pochette en visuel",
        "Un lien permanent à partager avec toute la famille et les amis",
        "Retrouvez votre musique à tout moment, même en changeant de téléphone",
        "Idéale pour les diaporamas souvenirs et les vidéos d'événements",
        "Visible dans le monde entier : parfait pour les proches à l'étranger",
        "Titre et description optimisés pour retrouver facilement la chanson",
        "Aucune manipulation technique de votre côté : nous nous occupons de tout",
        "Compatible avec le partage WhatsApp, Facebook, Instagram et par email",
        "Un souvenir numérique pérenne qui ne se perd jamais",
        "Service optionnel activable en un clic lors de la commande",
      ],
    },
    en: {
      title: "Publishing your song on YouTube",
      intro: "Want your personalized song to live online forever? We publish your music on YouTube: it becomes accessible to everyone, anytime, and you can share it with a single link.",
      details: [
        "Your song published on YouTube with its cover art as the visual",
        "A permanent link to share with family and friends",
        "Find your music anytime, even after switching phones",
        "Perfect for memory slideshows and event videos",
        "Visible worldwide: ideal for loved ones abroad",
        "Optimized title and description to easily find the song again",
        "No technical steps on your side: we handle everything",
        "Ready to share via WhatsApp, Facebook, Instagram and email",
        "A lasting digital keepsake that never gets lost",
        "Optional service enabled in one click at checkout",
      ],
    },
  },
  {
    id: "certificat-cadeau-qr", icon: Gift,
    fr: {
      title: "Certificat cadeau imprimable avec QR code",
      intro: "Comment offrir une chanson ? Avec notre certificat cadeau élégant et imprimable : un magnifique document personnalisé contenant un QR code qui mène directement à la musique. L'effet surprise est garanti au moment de l'ouverture.",
      details: [
        "Certificat cadeau élégant, prêt à imprimer chez vous en quelques secondes",
        "QR code intégré : la personne scanne et écoute immédiatement sa chanson",
        "Personnalisé avec le nom du destinataire et le titre de la chanson",
        "Parfait à glisser dans une carte, une enveloppe ou sous le sapin",
        "Un cadeau physique ET numérique à la fois : le meilleur des deux mondes",
        "Design soigné adapté aux mariages, anniversaires et fêtes",
        "Format A4 standard compatible avec toutes les imprimantes",
        "Réimprimable à volonté depuis votre espace personnel",
        "Idéal quand on ne peut pas être présent le jour J : le cadeau se suffit à lui-même",
        "Inclus dans le service cadeau, avec envoi possible par email au destinataire",
      ],
    },
    en: {
      title: "Printable gift certificate with QR code",
      intro: "How do you gift a song? With our elegant printable gift certificate: a beautiful personalized document with a QR code leading straight to the music. The surprise effect is guaranteed at unwrapping.",
      details: [
        "Elegant gift certificate, ready to print at home in seconds",
        "Embedded QR code: the recipient scans and listens to their song instantly",
        "Personalized with the recipient's name and the song title",
        "Perfect to slip into a card, an envelope or under the tree",
        "A physical AND digital gift at once: the best of both worlds",
        "Refined design suited to weddings, birthdays and celebrations",
        "Standard A4 format compatible with every printer",
        "Reprintable anytime from your personal account",
        "Ideal when you can't be there on the big day: the gift speaks for itself",
        "Included in the gift service, with optional email delivery to the recipient",
      ],
    },
  },
  {
    id: "pochette-album-ia", icon: ImageIcon,
    fr: {
      title: "Pochette d'album générée par IA",
      intro: "Chaque chanson personnalisée mérite son visuel. Notre IA crée une pochette d'album unique et artistique assortie à l'ambiance de votre musique — ou utilise votre propre photo pour un souvenir encore plus personnel.",
      details: [
        "Pochette d'album unique générée par intelligence artificielle",
        "Style visuel assorti à l'ambiance et au thème de votre chanson",
        "Option photo personnelle : votre propre image transformée en pochette",
        "Qualité haute résolution adaptée à l'impression",
        "Affichée sur la page d'écoute et sur YouTube",
        "Parfaite pour un rendu professionnel digne d'un vrai single",
        "Aucun coût de graphiste : le visuel est inclus dans la création",
        "Téléchargeable pour créer des supports souvenirs (cadres, cartes)",
        "Générée en même temps que la musique, sans délai supplémentaire",
        "Un ensemble complet : musique + paroles + visuel, prêt à offrir",
      ],
    },
    en: {
      title: "AI-generated album cover",
      intro: "Every personalized song deserves its artwork. Our AI creates a unique, artistic album cover matching the mood of your music — or uses your own photo for an even more personal keepsake.",
      details: [
        "Unique album cover generated by artificial intelligence",
        "Visual style matched to your song's mood and theme",
        "Personal photo option: your own image turned into cover art",
        "High-resolution quality suitable for printing",
        "Displayed on the listening page and on YouTube",
        "A professional single-worthy result",
        "No designer fees: the artwork is included with the creation",
        "Downloadable to create keepsakes (frames, cards)",
        "Generated together with the music, no extra delay",
        "A complete package: music + lyrics + artwork, ready to gift",
      ],
    },
  },
  {
    id: "abonnements-mensuels", icon: Repeat,
    fr: {
      title: "Abonnements mensuels : Pack Starter, Premium, Pro et Royal",
      intro: "Vous créez régulièrement des chansons personnalisées ? Nos quatre abonnements mensuels offrent un quota de musiques chaque mois à tarif dégressif : plus vous créez, moins chaque musique vous coûte.",
      details: [
        "Pack Starter : 5 chansons personnalisées chaque mois, idéal pour débuter",
        "Pack Premium : 10 chansons par mois pour les créateurs réguliers",
        "Pack Pro : 20 chansons par mois, parfait pour les animateurs et professionnels",
        "Pack Royal : 50 chansons par mois, le tarif par musique le plus bas",
        "Tarif dégressif : plus le pack est grand, moins chaque musique coûte cher",
        "Téléchargement MP3 inclus dans chaque chanson couverte par l'abonnement",
        "Quota renouvelé automatiquement chaque mois",
        "Résiliation possible à tout moment, effective en fin de période",
        "Gestion simple de l'abonnement depuis votre espace personnel",
        "Paiement sécurisé par carte, Apple Pay ou Google Pay",
      ],
    },
    en: {
      title: "Monthly subscriptions: Pack Starter, Premium, Pro and Royal",
      intro: "Do you create personalized songs regularly? Our four monthly subscriptions include a monthly quota of songs at decreasing rates: the more you create, the less each song costs.",
      details: [
        "Pack Starter: 5 personalized songs every month, perfect to get started",
        "Pack Premium: 10 songs per month for regular creators",
        "Pack Pro: 20 songs per month, ideal for hosts and professionals",
        "Pack Royal: 50 songs per month, the lowest price per song",
        "Decreasing rates: the bigger the pack, the less each song costs",
        "MP3 download included with every song covered by the subscription",
        "Quota automatically renewed every month",
        "Cancel anytime, effective at the end of the period",
        "Easy subscription management from your personal account",
        "Secure payment by card, Apple Pay or Google Pay",
      ],
    },
  },
  {
    id: "essai-gratuit-galerie", icon: Sparkles,
    fr: {
      title: "Essai gratuit, galerie publique et partage",
      intro: "Écoutez avant de commander : testez gratuitement la création d'une chanson et parcourez notre galerie publique remplie de vraies créations de la communauté. Chaque chanson dispose de sa page d'écoute partageable.",
      details: [
        "Essai gratuit pour découvrir la qualité de nos chansons personnalisées",
        "Galerie publique avec de vraies créations à écouter librement",
        "Exemples dans tous les styles : rap, country, rock, pop et plus",
        "Page d'écoute dédiée et partageable pour chaque chanson créée",
        "Partage facile par lien sur WhatsApp, Facebook, Instagram et email",
        "Aucune carte bancaire requise pour l'essai gratuit",
        "Inspirez-vous des créations de la communauté avant la vôtre",
        "Lecture en streaming directement depuis le navigateur, sans installation",
        "Miniatures et pochettes visibles pour choisir votre style préféré",
        "La preuve par l'écoute : jugez la qualité par vous-même avant d'acheter",
      ],
    },
    en: {
      title: "Free trial, public gallery and sharing",
      intro: "Listen before you order: try creating a song for free and browse our public gallery full of real community creations. Every song gets its own shareable listening page.",
      details: [
        "Free trial to experience the quality of our personalized songs",
        "Public gallery with real creations to listen to freely",
        "Examples in every style: rap, country, rock, pop and more",
        "Dedicated shareable listening page for every created song",
        "Easy link sharing on WhatsApp, Facebook, Instagram and email",
        "No credit card required for the free trial",
        "Get inspired by community creations before making yours",
        "Streams right in the browser, nothing to install",
        "Thumbnails and cover art on display to pick your favorite style",
        "Proof by listening: judge the quality yourself before buying",
      ],
    },
  },
];

export default function Services() {
  const { language } = useI18n();
  const isEn = language === "en";
  const isAndroidApp = useIsAndroidApp();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: isEn ? "Kalypso Music services" : "Services Kalypso Music",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "ListItem", position: i + 1,
        item: { "@type": "Service", name: s[isEn ? "en" : "fr"].title, provider: { "@type": "Organization", name: "Kalypso Music" } },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: isEn ? "Home" : "Accueil", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={isEn
          ? "Services & Benefits | Personalized AI Songs, Custom Lyrics, MP3 for Life | Kalypso Music"
          : "Services & Avantages | Chanson personnalisée IA, paroles sur mesure, MP3 à vie | Kalypso Music"}
        description={isEn
          ? "Discover all Kalypso Music services: AI personalized songs delivered in minutes, custom lyrics, realistic sung vocals, lifetime MP3 download, voice message with your own voice, YouTube publishing, printable QR gift certificate, AI album cover and monthly subscriptions."
          : "Découvrez tous les services Kalypso Music : chanson personnalisée par IA livrée en quelques minutes, paroles sur mesure, voix chantée réaliste, MP3 téléchargeable à vie, message vocal avec votre propre voix, publication YouTube, certificat cadeau QR imprimable, pochette d'album IA et abonnements mensuels."}
        path="/services"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        <section className="border-b border-border/60 bg-card/30 py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Kalypso Music</p>
            <h1 className="mt-3 font-heading text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
              {isEn
                ? "All our services: personalized AI songs, from creation to gifting"
                : "Tous nos services : la chanson personnalisée par IA, de la création au cadeau"}
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              {isEn
                ? "Kalypso Music is the all-in-one platform to create a personalized song online: AI-written custom lyrics, realistic sung vocals mixed and mastered, lifetime MP3 download, your own voice inside the music, YouTube publishing and a printable QR gift certificate. The perfect musical gift for weddings, birthdays, births and every special occasion."
                : "Kalypso Music est la plateforme tout-en-un pour créer une chanson personnalisée en ligne : paroles sur mesure écrites par IA, voix chantée réaliste mixée et masterisée, MP3 téléchargeable à vie, votre propre voix dans la musique, publication YouTube et certificat cadeau QR imprimable. Le cadeau musical parfait pour mariages, anniversaires, naissances et toutes les grandes occasions."}
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-2">
              {SERVICES.map((s) => (
                <a key={s.id} href={`#${s.id}`}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground">
                  {s[isEn ? "en" : "fr"].title.split(":")[0].split(",")[0]}
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {SERVICES.map((s, idx) => {
              const c = s[isEn ? "en" : "fr"];
              return (
                <motion.article
                  key={s.id} id={s.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5 }}
                  className="scroll-mt-24"
                  data-testid={`service-section-${s.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <s.icon className="h-6 w-6" strokeWidth={1.6} />
                    </div>
                    <div>
                      <h2 className="font-heading text-lg font-semibold sm:text-xl">{c.title}</h2>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.intro}</p>
                    </div>
                  </div>
                  <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2.5 rounded-2xl border border-border bg-card p-6 sm:grid-cols-2">
                    {c.details.map((d, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground/85">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  {idx === 0 && !isAndroidApp && (
                    <p className="mt-4 text-sm text-muted-foreground">
                      {isEn
                        ? <>Flat price: <strong className="text-foreground">$9.99 per complete song</strong> — no subscription required.</>
                        : <>Prix unique : <strong className="text-foreground">9,99 € la chanson complète</strong> — sans aucun abonnement obligatoire.</>}
                    </p>
                  )}
                  {s.id === "abonnements-mensuels" && !isAndroidApp && (
                    <p className="mt-4 text-sm text-muted-foreground">
                      {isEn
                        ? <>Plans: <strong className="text-foreground">Pack Starter</strong> 5 songs $19.99/mo ($3.99/song) · <strong className="text-foreground">Pack Premium</strong> 10 songs $29.99/mo ($2.99/song) · <strong className="text-foreground">Pack Pro</strong> 20 songs $39.99/mo ($1.99/song) · <strong className="text-foreground">Pack Royal</strong> 50 songs $49.99/mo ($0.99/song).</>
                        : <>Formules : <strong className="text-foreground">Pack Starter</strong> 5 chansons 19,99 €/mois (3,99 € la musique) · <strong className="text-foreground">Pack Premium</strong> 10 chansons 29,99 €/mois (2,99 € la musique) · <strong className="text-foreground">Pack Pro</strong> 20 chansons 39,99 €/mois (1,99 € la musique) · <strong className="text-foreground">Pack Royal</strong> 50 chansons 49,99 €/mois (0,99 € la musique).</>}
                    </p>
                  )}
                </motion.article>
              );
            })}
          </div>

          <div className="mt-20 rounded-3xl border border-primary/25 bg-primary/5 p-8 text-center sm:p-12">
            <h2 className="font-heading text-xl font-semibold sm:text-2xl">
              {isEn ? "Ready to create your personalized song?" : "Prêt à créer votre chanson personnalisée ?"}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
              {isEn
                ? "Wedding song, birthday song, birth song, original gift idea: create your custom AI song in minutes and download your MP3 for life."
                : "Chanson de mariage, chanson d'anniversaire, chanson de naissance, idée cadeau originale : créez votre chanson IA sur mesure en quelques minutes et téléchargez votre MP3 à vie."}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/create">
                <Button size="lg" className="rounded-full" data-testid="services-cta-create">
                  {isEn ? "Create my song now" : "Composer ma musique maintenant"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/galerie">
                <Button size="lg" variant="outline" className="rounded-full" data-testid="services-cta-gallery">
                  {isEn ? "Listen to examples" : "Écouter des exemples"}
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <Link to="/musique-mariage" className="hover:text-foreground">{isEn ? "Wedding song" : "Chanson de mariage"}</Link>·
              <Link to="/creer-chanson-personnalisee" className="hover:text-foreground">{isEn ? "Create a custom song" : "Créer une chanson personnalisée"}</Link>·
              <Link to="/cadeau-musical-personnalise" className="hover:text-foreground">{isEn ? "Musical gift" : "Cadeau musical personnalisé"}</Link>·
              <Link to="/votre-histoire-en-musique" className="hover:text-foreground">{isEn ? "Your story in music" : "Votre histoire en musique"}</Link>·
              <Link to="/musique-anniversaire" className="hover:text-foreground">{isEn ? "Birthday song" : "Chanson d'anniversaire"}</Link>·
              <Link to="/musique-naissance" className="hover:text-foreground">{isEn ? "Birth song" : "Chanson de naissance"}</Link>·
              <Link to="/idee-cadeau-original" className="hover:text-foreground">{isEn ? "Original gift idea" : "Idée cadeau originale"}</Link>·
              <Link to="/message-vocal-personnalise" className="hover:text-foreground">{isEn ? "Personalized voice message" : "Message vocal personnalisé"}</Link>·
              <Link to="/faq" className="hover:text-foreground">FAQ</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
